import { BOARD_SPACES } from '../shared/boardSpaces.js';
import { SpaceType, GameState, OwnedProperty, PropertySpace, RailroadSpace, UtilitySpace, TaxSpace } from '../shared/types.js';
import { $ } from './utils.js';


const SPACE_ICONS: Partial<Record<SpaceType, string>> = {
  [SpaceType.GO]: 'GO',
  [SpaceType.JAIL]: 'JAIL',
  [SpaceType.FREE_PARKING]: 'FREE',
  [SpaceType.GO_TO_JAIL]: 'GO TO JAIL',
  [SpaceType.CHANCE]: '?',
  [SpaceType.INCOME_TAX]: 'TAX',
  [SpaceType.LUXURY_TAX]: 'TAX',
  [SpaceType.RAILROAD]: 'RR',
  [SpaceType.UTILITY]: 'U',
};

function getSide(pos: number): string {
  if (pos === 0 || pos === 10 || pos === 20 || pos === 30) return 'corner';
  if (pos >= 1 && pos <= 9) return 'side-bottom';
  if (pos >= 11 && pos <= 19) return 'side-left';
  if (pos >= 21 && pos <= 29) return 'side-top';
  if (pos >= 31 && pos <= 39) return 'side-right';
  return '';
}

export function initBoardRenderer(): void {
  const board = $('#board');

  BOARD_SPACES.forEach((space) => {
    const div = document.createElement('div');
    div.className = `space ${getSide(space.position)}`;
    if (getSide(space.position) === 'corner') div.classList.add('corner');
    div.dataset.pos = String(space.position);

    // Color band for properties
    if (space.type === SpaceType.PROPERTY) {
      div.classList.add(`prop-${space.color}`);
      const band = document.createElement('div');
      band.className = `color-band color-${space.color}`;
      div.appendChild(band);
    }

    // Houses container
    const housesContainer = document.createElement('div');
    housesContainer.className = 'houses-container';
    housesContainer.id = `houses-${space.position}`;
    div.appendChild(housesContainer);

    // Tokens container
    const tokensContainer = document.createElement('div');
    tokensContainer.className = 'tokens-container';
    tokensContainer.id = `tokens-${space.position}`;
    div.appendChild(tokensContainer);

    // Space content
    const isCorner = getSide(space.position) === 'corner';
    const icon = SPACE_ICONS[space.type];

    if (isCorner) {
      const nameEl = document.createElement('div');
      nameEl.className = 'space-name';
      nameEl.textContent = space.name;
      div.appendChild(nameEl);
    } else if (space.type === SpaceType.PROPERTY || space.type === SpaceType.RAILROAD || space.type === SpaceType.UTILITY) {
      const nameEl = document.createElement('div');
      nameEl.className = 'space-name';
      // Abbreviate long names
      const name = space.name.length > 12 ? space.name.replace(' Avenue', ' Ave').replace(' Railroad', ' RR').replace(' Place', ' Pl') : space.name;
      nameEl.textContent = name;
      div.appendChild(nameEl);

      const priceEl = document.createElement('div');
      priceEl.className = 'space-price';
      const price = (space as PropertySpace | RailroadSpace | UtilitySpace).price;
      priceEl.textContent = `$${price}`;
      div.appendChild(priceEl);
    } else {
      if (icon) {
        const iconEl = document.createElement('div');
        iconEl.className = 'space-icon';
        iconEl.textContent = icon;
        div.appendChild(iconEl);
      }
      if (!icon || icon !== space.name) {
        const nameEl = document.createElement('div');
        nameEl.className = 'space-name';
        nameEl.textContent = space.name;
        div.appendChild(nameEl);
      }
      if (space.type === SpaceType.INCOME_TAX || space.type === SpaceType.LUXURY_TAX) {
        const priceEl = document.createElement('div');
        priceEl.className = 'space-price';
        priceEl.textContent = `$${(space as TaxSpace).amount}`;
        div.appendChild(priceEl);
      }
    }

    board.appendChild(div);
  });
}

// Diff caches \u2014 keyed by position / playerId so we only touch DOM nodes that changed.
type SpaceCache = { ownerColor: string | null; isMortgaged: boolean; houses: number };
const spaceCache = new Map<number, SpaceCache>();
const tokenElems = new Map<string, HTMLElement>();
const tokenState = new Map<string, { position: number; isCurrent: boolean; isBankrupt: boolean }>();
let lastDie1 = '';
let lastDie2 = '';
let lastPot = -1;

export function updateBoard(state: GameState): void {
  const playerById = new Map(state.players.map(p => [p.id, p]));

  // Per-property: ownership color, mortgage, houses.
  for (const prop of state.properties) {
    const owner = prop.ownerId ? playerById.get(prop.ownerId) : undefined;
    const ownerColor = owner ? owner.color : null;
    const cached = spaceCache.get(prop.position);

    if (
      cached &&
      cached.ownerColor === ownerColor &&
      cached.isMortgaged === prop.isMortgaged &&
      cached.houses === prop.houses
    ) {
      continue;
    }

    const spaceEl = document.querySelector(`.space[data-pos="${prop.position}"]`) as HTMLElement | null;
    if (!spaceEl) continue;

    if (!cached || cached.ownerColor !== ownerColor) {
      if (ownerColor) {
        spaceEl.classList.add('owned');
        spaceEl.style.setProperty('--owner-color', ownerColor);
      } else {
        spaceEl.classList.remove('owned');
        spaceEl.style.removeProperty('--owner-color');
      }
    }
    if (!cached || cached.isMortgaged !== prop.isMortgaged) {
      spaceEl.classList.toggle('mortgaged', prop.isMortgaged);
    }
    if (!cached || cached.houses !== prop.houses) {
      updateHouses(prop);
    }

    spaceCache.set(prop.position, { ownerColor, isMortgaged: prop.isMortgaged, houses: prop.houses });
  }

  updateTokens(state, playerById);

  // Free Parking pot \u2014 only write when value actually changes.
  if (state.freeParkingPot !== lastPot) {
    lastPot = state.freeParkingPot;
    const potDisplay = document.getElementById('pot-display');
    if (potDisplay) {
      potDisplay.textContent = `Free Parking: $${state.freeParkingPot.toLocaleString()}`;
    }
  }

  if (state.lastDiceRoll) {
    const v1 = String(state.lastDiceRoll[0]);
    const v2 = String(state.lastDiceRoll[1]);
    if (v1 !== lastDie1) { $('#die-1').textContent = v1; lastDie1 = v1; }
    if (v2 !== lastDie2) { $('#die-2').textContent = v2; lastDie2 = v2; }
  }
}

function updateHouses(prop: OwnedProperty): void {
  const container = document.getElementById(`houses-${prop.position}`);
  if (!container) return;
  container.innerHTML = '';

  if (prop.houses === 5) {
    const hotel = document.createElement('div');
    hotel.className = 'hotel';
    container.appendChild(hotel);
  } else {
    for (let i = 0; i < prop.houses; i++) {
      const house = document.createElement('div');
      house.className = 'house';
      container.appendChild(house);
    }
  }
}

function updateTokens(state: GameState, playerById: Map<string, GameState['players'][number]>): void {
  const currentPlayerId = state.players[state.currentPlayerIndex]?.id;
  const seen = new Set<string>();

  for (const player of state.players) {
    seen.add(player.id);
    const isCurrent = player.id === currentPlayerId;
    const cached = tokenState.get(player.id);

    // Bankrupt: drop token if present.
    if (player.isBankrupt) {
      const el = tokenElems.get(player.id);
      if (el) {
        el.remove();
        tokenElems.delete(player.id);
      }
      tokenState.set(player.id, { position: player.position, isCurrent, isBankrupt: true });
      continue;
    }

    let el = tokenElems.get(player.id);
    if (!el) {
      // First sighting \u2014 build the token element once and cache it.
      el = document.createElement('div');
      el.className = 'player-token';
      if (player.isBot) el.classList.add('bot');
      el.title = player.isBot ? `${player.name} (NPC)` : player.name;
      el.style.setProperty('--token-bg', player.color);
      const glyph = document.createElement('span');
      glyph.className = 'token-glyph';
      glyph.textContent = player.avatar || '\u25CF';
      el.appendChild(glyph);
      tokenElems.set(player.id, el);
    }

    // Reparent only when position changes.
    if (!cached || cached.position !== player.position || cached.isBankrupt) {
      const container = document.getElementById(`tokens-${player.position}`);
      if (container) container.appendChild(el);
    }
    if (!cached || cached.isCurrent !== isCurrent) {
      el.classList.toggle('current-turn', isCurrent);
    }

    tokenState.set(player.id, { position: player.position, isCurrent, isBankrupt: false });
  }

  // Drop tokens for players who left the state entirely (rare).
  for (const id of Array.from(tokenElems.keys())) {
    if (!seen.has(id)) {
      tokenElems.get(id)?.remove();
      tokenElems.delete(id);
      tokenState.delete(id);
    }
  }
}
